import html2canvas, { type Options } from 'html2canvas';

const SCREENSHOT_PILL_SELECTOR = '[data-screenshot-pill]';

const PILL_HEIGHT_BY_SIZE: Record<string, number> = {
  lg: 44,
  md: 36,
  sm: 28,
};

const PILL_FONT_SIZE_BY_SIZE: Record<string, number> = {
  lg: 22,
  md: 18,
  sm: 12,
};

function getCardBackgroundColor(): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--bg-card').trim();
  return value || '#ffffff';
}

function resolveCssColor(doc: Document, colorValue: string): string {
  if (!colorValue) return '';
  if (!colorValue.startsWith('var(')) return colorValue;

  const probe = doc.createElement('span');
  probe.style.color = colorValue;
  probe.style.display = 'none';
  doc.body.appendChild(probe);
  const resolved = doc.defaultView?.getComputedStyle(probe).color ?? '';
  doc.body.removeChild(probe);
  return resolved;
}

function getPillTextColor(doc: Document, el: HTMLElement): string {
  const preResolved = el.dataset.screenshotPillResolvedColor;
  if (preResolved) return preResolved;

  const computedColor = doc.defaultView?.getComputedStyle(el).color ?? '';
  if (computedColor && computedColor !== 'rgba(0, 0, 0, 0)') {
    return computedColor;
  }

  const fromDataset = el.dataset.screenshotPillColor ?? '';
  const resolvedDataset = resolveCssColor(doc, fromDataset);
  if (resolvedDataset) return resolvedDataset;

  const fromInline = el.style.color ?? '';
  const resolvedInline = resolveCssColor(doc, fromInline);
  if (resolvedInline) return resolvedInline;

  return '#111827';
}

function stampResolvedPillColors(element: HTMLElement): void {
  element.querySelectorAll(SCREENSHOT_PILL_SELECTOR).forEach((node) => {
    const el = node as HTMLElement;
    el.dataset.screenshotPillResolvedColor = getComputedStyle(el).color;
  });
}

function clearResolvedPillColors(element: HTMLElement): void {
  element.querySelectorAll(SCREENSHOT_PILL_SELECTOR).forEach((node) => {
    delete (node as HTMLElement).dataset.screenshotPillResolvedColor;
  });
}

function replacePillWithSvg(doc: Document, el: HTMLElement): void {
  const text = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  if (!text) return;

  const size = el.dataset.screenshotPillSize ?? 'lg';
  const height = PILL_HEIGHT_BY_SIZE[size] ?? PILL_HEIGHT_BY_SIZE.lg;
  const fontSize = PILL_FONT_SIZE_BY_SIZE[size] ?? PILL_FONT_SIZE_BY_SIZE.lg;
  const width = Math.max(el.offsetWidth, el.scrollWidth, 112);
  const color = getPillTextColor(doc, el);

  el.innerHTML = '';
  el.style.display = 'block';
  el.style.boxSizing = 'border-box';
  el.style.height = `${height}px`;
  el.style.width = `${width}px`;
  el.style.padding = '0';
  el.style.lineHeight = 'normal';
  el.style.overflow = 'hidden';

  const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', text);

  const textEl = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
  textEl.setAttribute('x', String(width / 2));
  textEl.setAttribute('y', String(height / 2));
  textEl.setAttribute('dominant-baseline', 'middle');
  textEl.setAttribute('text-anchor', 'middle');
  textEl.setAttribute('fill', color);
  textEl.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
  textEl.setAttribute('font-size', String(fontSize));
  textEl.setAttribute('font-weight', '800');
  textEl.textContent = text;

  svg.appendChild(textEl);
  el.appendChild(svg);
}

function prepareScreenshotClone(doc: Document, element: HTMLElement): void {
  element.querySelectorAll(SCREENSHOT_PILL_SELECTOR).forEach((node) => {
    replacePillWithSvg(doc, node as HTMLElement);
  });
}

function formatJalaliDateTime(date: Date = new Date()): string {
  const dateStr = date.toLocaleDateString('fa-IR');
  const timeStr = date.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  // LTR mark keeps visual order as date then time (avoids RTL bidi swap)
  return `\u200E${dateStr} - ${timeStr}`;
}

function stampDateFooter(source: HTMLCanvasElement): HTMLCanvasElement {
  const stamped = document.createElement('canvas');
  stamped.width = source.width;
  stamped.height = source.height;

  const ctx = stamped.getContext('2d');
  if (!ctx) return source;

  ctx.drawImage(source, 0, 0);

  const label = formatJalaliDateTime();
  const fontSize = Math.min(
    42,
    Math.max(22, Math.round(source.height * 0.028)),
  );
  const paddingX = Math.round(fontSize * 0.85);
  const paddingY = Math.round(fontSize * 0.5);
  const margin = Math.round(fontSize * 0.55);
  const radius = Math.round(fontSize * 0.35);

  ctx.font = `700 ${fontSize}px Tahoma, Arial, sans-serif`;
  ctx.direction = 'ltr';
  const textWidth = ctx.measureText(label).width;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  const boxX = margin;
  const boxY = margin;

  ctx.fillStyle = '#1f2937';
  ctx.beginPath();
  ctx.moveTo(boxX + radius, boxY);
  ctx.arcTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + boxHeight, radius);
  ctx.arcTo(boxX + boxWidth, boxY + boxHeight, boxX, boxY + boxHeight, radius);
  ctx.arcTo(boxX, boxY + boxHeight, boxX, boxY, radius);
  ctx.arcTo(boxX, boxY, boxX + boxWidth, boxY, radius);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, boxX + paddingX, boxY + boxHeight / 2);

  return stamped;
}

export interface CaptureElementScreenshotOptions {
  fileName?: string;
  scale?: number;
  backgroundColor?: string | null;
  onCapture?: (dataUrl: string) => void;
}

export async function captureElementScreenshot(
  element: HTMLElement,
  options: CaptureElementScreenshotOptions = {},
): Promise<string> {
  const {
    fileName,
    scale = 3,
    backgroundColor = getCardBackgroundColor(),
    onCapture,
  } = options;

  stampResolvedPillColors(element);

  try {
    const html2canvasOptions: Partial<Options> = {
      useCORS: true,
      allowTaint: true,
      scale,
      backgroundColor,
      logging: false,
      ignoreElements: (node) => node.classList?.contains('chart-control') ?? false,
      onclone: prepareScreenshotClone,
    };

    const canvas = await html2canvas(element, html2canvasOptions);
    const stampedCanvas = stampDateFooter(canvas);
    const dataUrl = stampedCanvas.toDataURL('image/png');
    onCapture?.(dataUrl);

    if (fileName) {
      const link = document.createElement('a');
      link.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
      link.href = dataUrl;
      link.click();
    }

    return dataUrl;
  } finally {
    clearResolvedPillColors(element);
  }
}
